import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { HelmetAsync } from '../components/HelmetAsync';
import { useReveal } from '../hooks/useReveal';
import { PrimaryButton, SectionLabel, Blob, Placeholder } from '../components/ui';
import { REQUIREMENT_OPTIONS, COMPANY } from '../data/content';
import { color, font, maxWidth } from '../styles/tokens';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const emptyForm = (requirement = 'General enquiry') => ({
  name: '',
  company: '',
  email: '',
  requirement,
  message: '',
});

const inputBase = {
  width: '100%',
  padding: '13px 14px',
  borderRadius: 11,
  font: `400 15px ${font.body}`,
  color: color.text,
  background: 'rgba(255,255,255,.03)',
};

export const ContactPage = () => {
  const location = useLocation();
  // Product-detail "Enquire" navigates here with a requirement in router state.
  const preset = location.state && location.state.requirement;

  const [form, setForm] = useState(() => emptyForm(preset || 'General enquiry'));
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [sentName, setSentName] = useState('');

  useReveal([sent]);

  const field = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const submit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const er = {};
    if (!form.name.trim()) er.name = 'Please enter your name';
    if (!form.email.trim()) er.email = 'Please enter your email';
    else if (!EMAIL_RE.test(form.email)) er.email = 'Enter a valid email address';
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    // NOTE: wire this to your real enquiry endpoint (e.g. POST to the API) here.
    setSentName(', ' + form.name.trim().split(' ')[0]);
    setSent(true);
  };

  const reset = () => {
    setForm(emptyForm());
    setErrors({});
    setSent(false);
  };

  const nameStyle = { ...inputBase, border: '1px solid ' + (errors.name ? color.danger : 'rgba(255,255,255,.16)') };
  const emailStyle = { ...inputBase, border: '1px solid ' + (errors.email ? color.danger : 'rgba(255,255,255,.16)') };
  const neutralBorder = { border: '1px solid rgba(255,255,255,.16)' };

  return (
    <div style={{ animation: 'vt-fade .4s ease' }}>
      <HelmetAsync
        title="Get in Touch — V-Tech Industries"
        description="Tell us what you're sourcing and our engineers will respond with specifications, lead time and pricing."
        keywords="V-Tech Industries contact, Get in Touch, honing tools enquiry, IMT Manesar"
        url="/contact"
      />

      {/* hero */}
      <section style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
        <Blob style={{ top: -140, left: -100, width: 520, height: 520, background: 'radial-gradient(circle,rgba(77,141,255,.4),transparent 64%)', opacity: 0.5 }} animation="vt-drift2 18s ease-in-out infinite" />
        <div style={{ position: 'relative', maxWidth, margin: '0 auto', padding: '72px 28px' }}>
          <SectionLabel data-reveal>[ GET IN TOUCH ]</SectionLabel>
          <h1 data-reveal data-delay="80ms" style={{ margin: '18px 0 0', font: `700 clamp(36px,5.4vw,64px)/0.98 ${font.display}`, letterSpacing: '-.03em', color: color.text }}>
            Get in Touch
          </h1>
          <p data-reveal data-delay="140ms" style={{ margin: '18px 0 0', maxWidth: 560, font: `400 16px/1.6 ${font.body}`, color: color.textMuted }}>
            Tell us what you're sourcing and our engineers will get back with specifications, lead time and pricing.
          </p>
        </div>
      </section>

      {/* form + contact card */}
      <section style={{ maxWidth, margin: '0 auto', padding: '64px 28px', display: 'flex', flexWrap: 'wrap', gap: 52, alignItems: 'flex-start' }}>
        {/* <div data-reveal style={{ flex: '1 1 440px', minWidth: 300 }}>
          {!sent ? (
            <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span style={{ font: `500 13px ${font.body}`, color: '#cdd4e0' }}>Name *</span>
                <input value={form.name} onChange={field('name')} placeholder="Your name" style={nameStyle} />
                {errors.name && <span style={{ font: `500 12px ${font.body}`, color: color.danger }}>{errors.name}</span>}
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span style={{ font: `500 13px ${font.body}`, color: '#cdd4e0' }}>Company</span>
                <input value={form.company} onChange={field('company')} placeholder="Company name" style={{ ...inputBase, ...neutralBorder }} />
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span style={{ font: `500 13px ${font.body}`, color: '#cdd4e0' }}>Email *</span>
                <input value={form.email} onChange={field('email')} placeholder="you@company.com" style={emailStyle} />
                {errors.email && <span style={{ font: `500 12px ${font.body}`, color: color.danger }}>{errors.email}</span>}
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span style={{ font: `500 13px ${font.body}`, color: '#cdd4e0' }}>Requirement</span>
                <select value={form.requirement} onChange={field('requirement')} style={{ ...inputBase, ...neutralBorder }}>
                  {REQUIREMENT_OPTIONS.map((o) => (
                    <option key={o} value={o} style={{ background: '#0d1017', color: color.text }}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span style={{ font: `500 13px ${font.body}`, color: '#cdd4e0' }}>Message</span>
                <textarea
                  value={form.message}
                  onChange={field('message')}
                  rows={4}
                  placeholder="Quantities, dimensions, application, target dates…"
                  style={{ ...inputBase, ...neutralBorder, font: `400 15px/1.5 ${font.body}`, resize: 'vertical' }}
                />
              </label>

              <PrimaryButton type="submit" style={{ gridColumn: '1 / -1', justifySelf: 'start', padding: '16px 32px' }}>
                Send enquiry
              </PrimaryButton>
            </form>
          ) : (
            <div style={{ background: 'rgba(77,141,255,.08)', border: '1px solid rgba(77,141,255,.3)', borderRadius: 20, padding: 44, textAlign: 'center', animation: 'vt-fade .4s ease' }}>
              <div style={{ width: 66, height: 66, margin: '0 auto', borderRadius: '50%', background: color.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', font: `700 30px ${font.display}`, color: color.bg, boxShadow: '0 0 40px -4px rgba(77,141,255,.7)' }}>
                ✓
              </div>
              <h2 style={{ margin: '24px 0 0', font: `600 26px ${font.display}`, color: color.text }}>Enquiry received</h2>
              <p style={{ margin: '12px 0 0', font: `400 15px/1.6 ${font.body}`, color: color.textMuted }}>
                Thanks{sentName} — our team will respond within one business day at the email you provided.
              </p>
              <button onClick={reset} style={{ marginTop: 26, background: 'rgba(255,255,255,.03)', color: color.text, border: '1px solid rgba(255,255,255,.16)', borderRadius: 999, padding: '13px 24px', font: `600 14px ${font.display}`, cursor: 'pointer' }}>
                Send another enquiry
              </button>
            </div>
          )}
        </div> */}

        <div data-reveal data-delay="0ms" style={{ flex: '1 1 300px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: 'rgba(255,255,255,.025)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 18, padding: 26 }}>
            <div style={{ font: `500 12px ${font.mono}`, letterSpacing: '.12em', color: color.accent }}>V-TECH INDUSTRIES</div>
            <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 16, font: `400 14px/1.5 ${font.body}`, color: '#cdd4e0' }}>
              <div>
                <div style={{ fontWeight: 600, color: color.text }}>Address</div>
                {COMPANY.address}
              </div>
              <div>
                <div style={{ fontWeight: 600, color: color.text }}>Email</div>
                <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
              </div>
              <div>
                <div style={{ fontWeight: 600, color: color.text }}>Phone</div>
                {COMPANY.phones[0]}
                <br />
                {COMPANY.phones[1]}
              </div>
              {/* <div>
                <div style={{ fontWeight: 600, color: color.text }}>Web</div>
                <a href={COMPANY.web}>{COMPANY.webLabel}</a>
              </div> */}
            </div>
          </div>
          <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3510.661828486012!2d76.9295425!3d28.369071899999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d3c380148c573%3A0xce52f1e08aee0a19!2sV-Tech%20Industries!5e0!3m2!1sen!2sin!4v1786145877585!5m2!1sen!2sin" width="full" style={{aspectRatio: '20/10', borderRadius: 18 }} allowFullScreen="" loading="lazy" referrerPolicy="strict-origin-when-cross-origin"></iframe>
          {/* <Placeholder label="map · IMT Manesar" style={{ aspectRatio: '16/10', borderRadius: 18 }} /> */}
        </div>
      </section>
    </div>
  );
};
